import { normalizeRecurrenceId } from './eventRecurrence.js';
export const jamRoomName = (postId, _recurrenceId) => postId;
const UUID_PREFIX = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:_.*)?$/i;
export const postIdFromJamRoomName = (roomName) => {
    const match = roomName.match(UUID_PREFIX);
    return match?.[1] ?? roomName;
};
/** Recurring jams use one LiveKit room per event; leftover occurrence rooms still collapse. */
export const uniquePostsById = (posts) => {
    const seen = new Set();
    const unique = [];
    for (const post of posts) {
        if (!post || seen.has(post.id))
            continue;
        seen.add(post.id);
        unique.push(post);
    }
    return unique;
};
export const getJamUrl = (id, origin, occurrence) => {
    if (!id) {
        return '';
    }
    const query = occurrence
        ? `?occurrence=${encodeURIComponent(normalizeRecurrenceId(occurrence))}`
        : '';
    if (!origin)
        return `/events/${id}/jam${query}`;
    return `${origin}/events/${id}/jam${query}`;
};
export const jamFromEvent = (event) => {
    if (event.data?.type === 'event' && event.data?.jam) {
        return event.data.jam;
    }
    return undefined;
};
export const canModerateJam = (profile, post) => !!(profile && jamFromEvent(post)?.moderators?.includes(profile.id));
/** Host or jam moderator — same gate as the event Attendees tab. */
export const canViewJamAttendees = (profile, post) => !!(profile &&
    (post.profile.id === profile.id || canModerateJam(profile, post)));
export const canAccessJamRecordings = (profile, post) => {
    if (!profile) {
        return false;
    }
    if (post.profile.id === profile.id) {
        return true;
    }
    if (post.data?.type !== 'event') {
        return false;
    }
    const event = post.data;
    if (event.moderators?.includes(profile.id)) {
        return true;
    }
    return !!event.jam?.moderators?.includes(profile.id);
};
export const jamRecordingAcceptsUpload = (status) => status === 'requested' || status === 'active' || status === 'finalizing';
export const isRtmpJamRecording = (recording) => recording.kind === 'rtmp';
export const isFileJamRecording = (recording) => !isRtmpJamRecording(recording);
export const pickActiveFileRecording = (recordings) => recordings.find(isFileJamRecording);
export const pickActiveRtmpStream = (recordings) => recordings.find(isRtmpJamRecording);
export const assembleRtmpUrl = (url, streamKey) => {
    const base = url.trim().replace(/\/+$/, '');
    const key = streamKey.trim().replace(/^\/+/, '');
    if (!base || !key) {
        return undefined;
    }
    if (!/^rtmps?:\/\//i.test(base)) {
        return undefined;
    }
    return `${base}/${key}`;
};
export const rtmpDestinationHost = (rtmpUrl) => {
    try {
        return new URL(rtmpUrl).hostname || undefined;
    }
    catch {
        return undefined;
    }
};
export const toRtmpStreamResponse = (recording) => ({
    id: recording.id,
    status: recording.status,
    destinationHost: recording.destinationHost,
    egressId: recording.egressId,
});
/** Epoch ms from participant metadata when their hand is up. */
const raisedHandTimestamp = (metadata) => {
    if (!metadata)
        return undefined;
    try {
        const handRaised = JSON.parse(metadata)
            .handRaised;
        if (typeof handRaised !== 'string' || handRaised.length === 0) {
            return undefined;
        }
        const timestamp = Date.parse(handRaised);
        return Number.isNaN(timestamp) ? undefined : timestamp;
    }
    catch {
        return undefined;
    }
};
/**
 * Raised hands first, most recent raise first, so the person who just raised
 * moves to the top of the people list and the top-left of the video grid.
 * Everyone else keeps their relative order.
 */
export const sortByRaisedHand = (items, metadataOf) => [...items].sort((a, b) => {
    const aRaised = raisedHandTimestamp(metadataOf(a));
    const bRaised = raisedHandTimestamp(metadataOf(b));
    if (aRaised === undefined && bRaised === undefined)
        return 0;
    if (aRaised === undefined)
        return 1;
    if (bRaised === undefined)
        return -1;
    return bRaised - aRaised;
});
